// client/src/components/EditPOForm.jsx
//
// In-place edit form for an existing PO. Used by PODetail.jsx — replaces the
// read-only view when the user clicks "Edit." Calls the existing PUT
// /api/pos/:id endpoint, which already enforces field-level permissions
// server-side (drafts: full edit; sent/issued: status/notes/proof/shipping only).
//
// Design choice: full edit of every field exposed by the backend for drafts.
// For non-drafts, we still show the form but disable the restricted fields
// with a hint. This makes the limitations transparent rather than the user
// hitting a 400 after typing.

import { useState, useMemo, useEffect } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;
const SHIPPING_OPTIONS = ['Ground', '2-Day', 'Overnight', 'Customer Pickup', 'LTL Freight', 'Other'];

export default function EditPOForm({ po, getToken, onCancel, onSaved, showToast }) {
  const isDraft = po.status === 'draft';

  // Working copies of every editable field. Initialized from `po` so re-rendering
  // doesn't reset edits. Items + charges + ship_to are JSONB structures we mutate
  // through immutable patterns.
  const [items,            setItems]            = useState(() => normalizeItems(po.items));
  const [charges,          setCharges]          = useState(() => Array.isArray(po.charges) ? po.charges : []);
  const [shipTo,           setShipTo]           = useState(() => ({
    name: '', company: '', address1: '', address2: '',
    city: '', state: '', zip: '', phone: '',
    ...(po.ship_to || {}),
  }));
  const [inHandDate,       setInHandDate]       = useState(po.in_hand_date || '');
  const [decorationNotes,  setDecorationNotes]  = useState(po.decoration_notes || '');
  const [terms,            setTerms]            = useState(po.terms || '');
  const [notes,            setNotes]            = useState(po.notes || '');
  const [externalNotes,    setExternalNotes]    = useState(po.external_notes || '');
  const [accountNumber,    setAccountNumber]    = useState(po.account_number || '');
  const [clientName,       setClientName]       = useState(po.client_name || '');
  const [proofRequired,    setProofRequired]    = useState(po.proof_required ?? true);
  const [shippingMethod,   setShippingMethod]   = useState(po.shipping_method || 'Ground');
  // "Ship on our account" — '' means vendor ships & bills us.
  const [shipAccountCarrier, setShipAccountCarrier] = useState(po.ship_account_carrier || '');
  const [carrierAccounts,  setCarrierAccounts]  = useState({});
  const [saving, setSaving] = useState(false);

  // Load which carrier accounts are configured so we only offer set-up carriers.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await getToken();
        const data = await api.withToken(token).getPOSettings();
        if (alive) setCarrierAccounts(data?.po_account_info?.carrier_accounts || {});
      } catch { /* leave empty — picker just won't show */ }
    })();
    return () => { alive = false; };
  }, [getToken]);

  // Subtotal/total — computed for display only. Server recomputes authoritatively.
  const { subtotal, total } = useMemo(() => {
    const sub = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_cost) || 0), 0);
    const chargesTotal = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    return { subtotal: +sub.toFixed(2), total: +(sub + chargesTotal).toFixed(2) };
  }, [items, charges]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      // Always include each item's line_total so the server's recompute lines up
      // with what the user saw in the UI.
      const itemsWithTotals = items.map(it => ({
        ...it,
        qty:        Number(it.qty) || 0,
        unit_cost:  Number(it.unit_cost) || 0,
        line_total: +((Number(it.qty) || 0) * (Number(it.unit_cost) || 0)).toFixed(2),
      }));
      // For drafts, send the whole payload. For non-drafts, the server will
      // discard everything except the allow-listed fields, so it's safe to
      // send the full thing — but for clarity we trim the body to what's
      // actually editable in this state.
      const payload = isDraft ? {
        items:            itemsWithTotals,
        charges,
        ship_to:          shipTo,
        in_hand_date:     inHandDate || null,
        decoration_notes: decorationNotes,
        terms,
        notes,
        external_notes:   externalNotes,
        account_number:   accountNumber,
        client_name:      clientName,
        proof_required:   proofRequired,
        shipping_method:  shippingMethod,
        ship_account_carrier: shipAccountCarrier || null,
      } : {
        notes,
        external_notes:   externalNotes,
        proof_required:   proofRequired,
        shipping_method:  shippingMethod,
        ship_account_carrier: shipAccountCarrier || null,
      };
      const updated = await api.withToken(token).updatePO(po.id, payload);
      showToast?.('PO updated', 'success');
      onSaved?.(updated);
    } catch (e) {
      showToast?.(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Item row mutations ────────────────────────────────────────────────
  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };
  // ─── Charge row mutations ──────────────────────────────────────────────
  const updateCharge = (idx, patch) => {
    setCharges(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };
  const removeCharge = (idx) => {
    setCharges(prev => prev.filter((_, i) => i !== idx));
  };
  const addCharge = () => {
    setCharges(prev => [...prev, { description: '', amount: 0 }]);
  };

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 12, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            Editing PO
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginTop: 4 }}>{po.po_number}</div>
          {!isDraft && (
            <div style={{ fontSize: 11, color: '#92400E', marginTop: 6, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 10px', display: 'inline-block' }}>
              This PO has already been sent ({po.status}). Only notes, proof, and shipping method can be changed.
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} disabled={saving} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* ─── Client + account ─────────────────────────────────────────── */}
      <Section title="Client and account">
        <Row>
          <Field label="Client name">
            <input value={clientName} disabled={!isDraft}
              onChange={e => setClientName(e.target.value)}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
          <Field label="Account number">
            <input value={accountNumber} disabled={!isDraft}
              onChange={e => setAccountNumber(e.target.value)}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
        </Row>
      </Section>

      {/* ─── Items ────────────────────────────────────────────────────── */}
      <Section title="Items">
        {items.length === 0 && (
          <div style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: 20 }}>
            No items on this PO. {!isDraft && '(Items can only be edited on draft POs.)'}
          </div>
        )}
        {items.map((it, idx) => (
          <div key={idx} style={{ background: '#F8FAFC', borderRadius: 8, padding: 14, marginBottom: 10, border: '1px solid #E2E8F0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', flex: 1 }}>
                {it.name || it.product_name || it.description || '(unnamed item)'}
              </div>
              {isDraft && (
                <button onClick={() => removeItem(idx)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11, color: '#DC2626', borderColor: '#FECACA' }}>
                  Remove
                </button>
              )}
            </div>
            <Row>
              <Field label="Quantity">
                <input type="number" min="0" value={it.qty || 0} disabled={!isDraft}
                  onChange={e => updateItem(idx, { qty: e.target.value })}
                  style={isDraft ? inputStyle : inputDisabled} />
              </Field>
              <Field label="Unit cost">
                <input type="number" min="0" step="0.01" value={it.unit_cost || 0} disabled={!isDraft}
                  onChange={e => updateItem(idx, { unit_cost: e.target.value })}
                  style={isDraft ? inputStyle : inputDisabled} />
              </Field>
              <Field label="Line total">
                <div style={{ ...inputDisabled, fontSize: 13, color: '#0F172A', fontWeight: 500 }}>
                  ${((Number(it.qty) || 0) * (Number(it.unit_cost) || 0)).toFixed(2)}
                </div>
              </Field>
            </Row>
            {isDraft && (
              <Row>
                <Field label="Decoration locations">
                  <input value={it.decoration_locations || ''}
                    onChange={e => updateItem(idx, { decoration_locations: e.target.value })}
                    placeholder="Front center" style={inputStyle} />
                </Field>
                <Field label="Decoration colors">
                  <input value={it.decoration_colors || ''}
                    onChange={e => updateItem(idx, { decoration_colors: e.target.value })}
                    placeholder="1-color, navy" style={inputStyle} />
                </Field>
              </Row>
            )}
            {isDraft && (
              <Field label="Mockup / artwork filename">
                <input value={it.mockup_filename || ''}
                  onChange={e => updateItem(idx, { mockup_filename: e.target.value })}
                  placeholder="acme-logo-final.ai" style={inputStyle} />
              </Field>
            )}
          </div>
        ))}
      </Section>

      {/* ─── Additional charges ─────────────────────────────────────────── */}
      <Section title="Additional charges">
        {charges.map((c, idx) => (
          <Row key={idx}>
            <Field label="Description">
              <input value={c.description || ''} disabled={!isDraft}
                onChange={e => updateCharge(idx, { description: e.target.value })}
                style={isDraft ? inputStyle : inputDisabled} />
            </Field>
            <Field label="Amount">
              <input type="number" min="0" step="0.01" value={c.amount || 0} disabled={!isDraft}
                onChange={e => updateCharge(idx, { amount: e.target.value })}
                style={isDraft ? inputStyle : inputDisabled} />
            </Field>
            {isDraft && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button onClick={() => removeCharge(idx)} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, color: '#DC2626', borderColor: '#FECACA' }}>
                  Remove
                </button>
              </div>
            )}
          </Row>
        ))}
        {isDraft && (
          <button onClick={addCharge} style={{ ...btnSecondary, marginTop: 8 }}>+ Add charge</button>
        )}
      </Section>

      {/* ─── Ship to ────────────────────────────────────────────────────── */}
      <Section title="Ship to">
        <Row>
          <Field label="Contact name">
            <input value={shipTo.name} disabled={!isDraft}
              onChange={e => setShipTo({ ...shipTo, name: e.target.value })}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
          <Field label="Company">
            <input value={shipTo.company} disabled={!isDraft}
              onChange={e => setShipTo({ ...shipTo, company: e.target.value })}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
        </Row>
        <Field label="Address line 1">
          <input value={shipTo.address1} disabled={!isDraft}
            onChange={e => setShipTo({ ...shipTo, address1: e.target.value })}
            style={isDraft ? inputStyle : inputDisabled} />
        </Field>
        <Field label="Address line 2">
          <input value={shipTo.address2} disabled={!isDraft}
            onChange={e => setShipTo({ ...shipTo, address2: e.target.value })}
            style={isDraft ? inputStyle : inputDisabled} />
        </Field>
        <Row>
          <Field label="City">
            <input value={shipTo.city} disabled={!isDraft}
              onChange={e => setShipTo({ ...shipTo, city: e.target.value })}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
          <Field label="State">
            <input value={shipTo.state} disabled={!isDraft}
              onChange={e => setShipTo({ ...shipTo, state: e.target.value })}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
          <Field label="ZIP / Postal">
            <input value={shipTo.zip} disabled={!isDraft}
              onChange={e => setShipTo({ ...shipTo, zip: e.target.value })}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
        </Row>
        <Field label="Phone">
          <input value={shipTo.phone} disabled={!isDraft}
            onChange={e => setShipTo({ ...shipTo, phone: e.target.value })}
            style={isDraft ? inputStyle : inputDisabled} />
        </Field>
      </Section>

      {/* ─── Logistics / decoration ─────────────────────────────────────── */}
      <Section title="Logistics">
        <Row>
          <Field label="In-hand date">
            <input type="date" value={inHandDate} disabled={!isDraft}
              onChange={e => setInHandDate(e.target.value)}
              style={isDraft ? inputStyle : inputDisabled} />
          </Field>
          <Field label="Shipping method">
            <select value={shippingMethod} onChange={e => setShippingMethod(e.target.value)} style={inputStyle}>
              {SHIPPING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </Field>
        </Row>
        {(() => {
          const configured = ['ups', 'fedex'].filter(
            c => carrierAccounts?.[c]?.account_number && carrierAccounts?.[c]?.zip
          );
          if (configured.length === 0) return null;
          const LABEL = { ups: 'UPS', fedex: 'FedEx' };
          return (
            <Field label="Ship on our account">
              <select value={shipAccountCarrier} onChange={e => setShipAccountCarrier(e.target.value)} style={inputStyle}>
                <option value="">Vendor ships &amp; bills us</option>
                {configured.map(c => (
                  <option key={c} value={c}>{LABEL[c]} — bill freight to our account</option>
                ))}
              </select>
            </Field>
          );
        })()}
        <Field label="Proof required before production">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={proofRequired} onChange={e => setProofRequired(e.target.checked)} />
            Yes, require proof from supplier before producing
          </label>
        </Field>
        <Field label="Decoration notes">
          <textarea value={decorationNotes} disabled={!isDraft}
            onChange={e => setDecorationNotes(e.target.value)}
            rows={3} style={{ ...(isDraft ? inputStyle : inputDisabled), resize: 'vertical', minHeight: 70, fontFamily: 'inherit' }} />
        </Field>
        <Field label="Terms">
          <textarea value={terms} disabled={!isDraft}
            onChange={e => setTerms(e.target.value)}
            rows={2} style={{ ...(isDraft ? inputStyle : inputDisabled), resize: 'vertical', minHeight: 50, fontFamily: 'inherit' }} />
        </Field>
        <Field label="Notes to supplier (prints on the PO)">
          <textarea value={externalNotes}
            onChange={e => setExternalNotes(e.target.value)}
            rows={3} placeholder="Visible to the supplier on the PO."
            style={{ ...inputStyle, resize: 'vertical', minHeight: 70, fontFamily: 'inherit' }} />
        </Field>
        <Field label="Internal notes (private — not shown on the PO)">
          <textarea value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: 70, fontFamily: 'inherit' }} />
        </Field>
      </Section>

      {/* ─── Totals (display only) ──────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', marginBottom: 6 }}>
          <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', marginBottom: 6 }}>
          <span>Charges</span><span>${(total - subtotal).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 600, color: '#0F172A', borderTop: '1px solid #E2E8F0', paddingTop: 8, marginTop: 8 }}>
          <span>Total</span><span>${total.toFixed(2)}</span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onCancel} disabled={saving} style={btnSecondary}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={btnPrimary}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

// Items shapes vary — some POs have items keyed differently. This normalizes
// to a consistent {name, qty, unit_cost, line_total, decoration_*, ...} shape.
function normalizeItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map(it => ({
    ...it,
    qty:                  it.qty ?? it.quantity ?? 0,
    unit_cost:            it.unit_cost ?? it.unitCost ?? it.price ?? 0,
    name:                 it.name ?? it.product_name ?? it.description ?? '',
    decoration_locations: it.decoration_locations ?? '',
    decoration_colors:    it.decoration_colors ?? '',
    mockup_filename:      it.mockup_filename ?? '',
  }));
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{ flex: 1, marginBottom: 8 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', border: '1px solid #E2E8F0', borderRadius: 7,
  padding: '8px 11px', fontSize: 13, color: '#0F172A', outline: 'none',
  fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box',
};
const inputDisabled = {
  ...inputStyle, background: '#F8FAFC', color: '#94A3B8', cursor: 'not-allowed',
};
const btnPrimary = {
  background: G, border: 'none', borderRadius: 8, padding: '9px 18px',
  color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
};
const btnSecondary = {
  background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 14px',
  color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
};
